import React from 'react';
import {
  Box,
  Text,
  Button,
  VStack,
  Skeleton,
  Flex,
  HStack,
  Icon,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  Divider,
} from '@chakra-ui/react';
import { InfoIcon } from '@chakra-ui/icons';
import { getTaxIdOption } from '../../../utils/taxIdOptions';

const TaxIdDisplay = ({ taxId, isLoading, onEdit }) => {
  if (isLoading) {
    return (
      <Box>
        <Skeleton height="20px" width="100px" mb={2} />
        <Skeleton height="16px" width="200px" mb={1} />
        <Skeleton height="16px" width="150px" />
      </Box>
    );
  }

  if (!taxId) {
    return (
      <Box
        py={6}
        px={8}
        bg="white"
        borderRadius="15px"
        borderWidth="1px"
        height="100%"
      >
        <Text textStyle="heading3">Tax ID</Text>
        <Divider mt="10px" border="1px solid lightGrey" />
        <Text textStyle="bodyFamilyRegular" fontSize="14px" mt="20px">
          No tax ID on file
        </Text>
        <Button size="sm" variant="outline" mt="20px" w="100%" onClick={onEdit}>
          <Text>Add tax ID</Text>
        </Button>
      </Box>
    );
  }

  const taxIdOption = getTaxIdOption(taxId.type);
  const displayName = taxIdOption
    ? taxIdOption.label
    : taxId.type.toUpperCase();

  const getVerificationDisplay = () => {
    if (!taxId.verification) return null;

    const status = taxId.verification.status;
    let color = 'gray.500';
    let statusText = '';
    let description = '';

    switch (status) {
      case 'verified':
        color = 'green.600';
        statusText = 'Verified by government database';
        description =
          'This tax ID has been automatically verified against official government databases (VIES for EU VAT, HMRC for UK VAT, or ABR for Australian ABN).';
        break;
      case 'pending':
        color = 'yellow.600';
        statusText = 'Verification in progress';
        description =
          'We are currently verifying this tax ID against government databases. This process usually takes a few minutes.';
        break;
      case 'unavailable':
        color = 'gray.500';
        statusText =
          'Automatic verification is not available for this tax ID type';
        description =
          "Automatic verification is only available for EU VAT numbers, UK VAT numbers, and Australian ABN numbers. Other tax ID types cannot be verified automatically against government databases. If you're not from these regions, simply ensure your tax ID is correct and ignore this verification status.";
        break;
      case 'unverified':
        color = 'red.600';
        statusText = 'Could not be verified';
        description =
          'We attempted to verify this tax ID against government databases but verification failed. This could mean the tax ID is invalid or there was a temporary issue with the verification service.';
        break;
      default:
        statusText = 'Status unknown';
        description = 'The verification status of this tax ID is unknown.';
        break;
    }

    return (
      <Box mt={2} pt={2}>
        <HStack spacing={1} align="center">
          <Text fontSize="xs" color={color} fontWeight="medium">
            {statusText}
          </Text>
          <Popover trigger="hover" placement="right">
            <PopoverTrigger>
              <Icon
                as={InfoIcon}
                boxSize={3}
                color="gray.400"
                cursor="pointer"
              />
            </PopoverTrigger>
            <PopoverContent
              p={1}
              maxW="300px"
              bg="secondaryDark"
              borderRadius="5px"
            >
              <PopoverBody>
                <Text
                  textStyle="bodyFamilyRegular"
                  fontSize="12px"
                  color="white"
                >
                  {description}
                </Text>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        </HStack>
      </Box>
    );
  };

  return (
    <Box
      py={6}
      px={8}
      bg="white"
      borderRadius="15px"
      borderWidth="1px"
      height="100%"
    >
      <Text textStyle="heading3">Tax ID</Text>
      <Divider mt="10px" border="1px solid lightGrey" />
      <Flex justifyContent="space-between" alignItems="center" mt="20px">
        <Text textStyle="bodyFamilyRegular" fontSize="14px">
          {displayName}
        </Text>
        <Text textStyle="bodyFamilyRegular" fontSize="14px">
          {taxId.value}
        </Text>
      </Flex>
      {getVerificationDisplay()}
      <Button size="sm" variant="outline" mt="20px" w="100%" onClick={onEdit}>
        <Text>{taxId?.value ? 'Edit tax ID' : 'Add tax ID'}</Text>
      </Button>
    </Box>
  );
};

export default TaxIdDisplay;
